import type { LivePanelConfig, LiveField } from '../types';
import type { Device, DeviceTelemetry } from '../../types';
import { radToDeg } from '../../lib/units';

export interface LivePanelProps {
  panel: LivePanelConfig;                // { serial, field } — ignores the dashboard time range
  telemetry: Map<string, DeviceTelemetry>;  // the PAGE's single useMqtt connection state
  isConnected: boolean;                  // from the page-level useMqtt
  device?: Device;                       // from the page's fleet.listDevices() — API fallback text
}

// Per-joint arrays render as chips; joint_temperatures are °C, joint_positions
// are ° (mirrors DeviceDetail). Scalar fields get a unit suffix.
const ARRAY_UNIT: Partial<Record<LiveField, string>> = {
  joint_temperatures: '°C',
  joint_positions: '°',
};
const SCALAR_UNIT: Partial<Record<LiveField, string>> = {
  cycle_time_ms: 'ms',
  pick_success_rate: '%',
  power_watts: 'W',
  cpu_temp: '°C',
};

export function LivePanel({ panel, telemetry, isConnected, device }: LivePanelProps) {
  const live = telemetry.get(panel.serial);

  // MQTT-down is SILENT: never an error state. Fall back to API/device context.
  if (!live) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-4 text-center text-xs text-gray-500">
        {device ? (
          <>
            <span className="font-mono text-gray-400">{device.serial}</span>
            <span>Waiting for live telemetry — device is {device.status}</span>
          </>
        ) : (
          <>
            <span className="font-mono text-gray-400">{panel.serial}</span>
            <span>Waiting for live telemetry</span>
          </>
        )}
      </div>
    );
  }

  const v = live[panel.field];

  return (
    <div className="relative h-full flex flex-col items-center justify-center">
      {isConnected && (
        <span className="absolute top-1.5 right-2 flex items-center gap-1 text-[10px] text-emerald-400">
          ⚡ Live
        </span>
      )}
      {Array.isArray(v) ? (
        <div className="flex flex-wrap items-center justify-center gap-1.5 px-3">
          {v.map((n, i) => (
            <span
              key={i}
              className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-mono text-gray-300"
            >
              J{i + 1} {(panel.field === 'joint_positions' ? radToDeg(n) : n).toFixed(1)}{ARRAY_UNIT[panel.field] ?? ''}
            </span>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center">
          <span className="text-3xl font-semibold text-white">
            {v >= 100 ? v.toFixed(0) : v.toFixed(1)}
          </span>
          <span className="text-xs text-gray-500">{SCALAR_UNIT[panel.field] ?? ''}</span>
        </div>
      )}
    </div>
  );
}
